import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Plus, Edit2, Trash2, Calendar as CalendarIcon, Save, X, Upload } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  title: '',
  tagline: '',
  description: '',
  product_details: '',
  image_url: '',
  additional_images: '',
  price: 28.00,
  active: true,
  sort_order: 0,
  stock: null,
  pre_sale: false,
  ships_by: '',
  slug: ''
};

function AdminCalendar({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [salesStats, setSalesStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchProducts();
    fetchSalesStats();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/admin/calendar-products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesStats = async () => {
    try {
      const response = await axios.get('/admin/calendar-products/sales');
      setSalesStats(response.data);
    } catch (error) {
      console.error('Error fetching calendar sales stats:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post('/upload/image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingGallery(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post('/upload/image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const existing = formData.additional_images ? formData.additional_images.split('\n').filter(Boolean) : [];
      setFormData({ ...formData, additional_images: [...existing, response.data.url].join('\n') });
      toast.success('Photo added to gallery');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingGallery(false);
      e.target.value = '';
    }
  };

  const buildPayload = () => ({
    title: formData.title,
    tagline: formData.tagline || null,
    description: formData.description || null,
    product_details: formData.product_details
      ? formData.product_details.split('\n').map(s => s.trim()).filter(Boolean)
      : [],
    image_url: formData.image_url,
    additional_images: formData.additional_images
      ? formData.additional_images.split('\n').map(s => s.trim()).filter(Boolean)
      : [],
    price: formData.price,
    active: formData.active,
    sort_order: formData.sort_order,
    stock: formData.stock,
    pre_sale: formData.pre_sale,
    ships_by: formData.ships_by || null,
    slug: formData.slug || null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = buildPayload();
      if (editingProduct) {
        await axios.put(`/admin/calendar-products/${editingProduct.id}`, payload);
        toast.success('Calendar updated');
      } else {
        await axios.post('/admin/calendar-products', payload);
        toast.success('Calendar created');
      }

      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving calendar:', error);
      toast.error(error.response?.data?.detail || 'Failed to save calendar');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      tagline: product.tagline || '',
      description: product.description || '',
      product_details: (product.product_details || []).join('\n'),
      image_url: product.image_url || '',
      additional_images: (product.additional_images || []).join('\n'),
      price: product.price || 28.00,
      active: product.active !== false,
      sort_order: product.sort_order || 0,
      stock: product.stock ?? null,
      pre_sale: product.pre_sale || false,
      ships_by: product.ships_by || '',
      slug: product.slug || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this calendar?')) return;

    try {
      await axios.delete(`/admin/calendar-products/${productId}`);
      toast.success('Calendar deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting calendar:', error);
      toast.error('Failed to delete calendar');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData(emptyForm);
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="calendar">
      <div className="page-header">
        <h1>Calendar</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} /> Add Calendar
        </button>
      </div>

      {/* Product Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>{editingProduct ? 'Edit Calendar' : 'Add New Calendar'}</h2>
            <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., 2027 Another Mostly Good OBX Calendar"
              />
            </div>

            <div className="form-group">
              <label>Web Address</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>/shop/calendar/</span>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g., black-and-white"
                />
              </div>
              <small style={{ color: '#666', fontSize: '12px' }}>
                Gives this calendar its own sales page. The first calendar in the list (lowest Sort Order) is always at /shop/calendar too, so it can stay blank.
              </small>
            </div>

            <div className="form-group">
              <label>Tagline</label>
              <input
                type="text"
                className="form-input"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                placeholder="A short line shown under the title, e.g. a subtitle"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the calendar — this shows in the main text box on the sales page..."
                style={{ minHeight: '120px' }}
              />
            </div>

            <div className="form-group">
              <label>Product Details (one per line)</label>
              <textarea
                className="form-textarea"
                value={formData.product_details}
                onChange={(e) => setFormData({ ...formData, product_details: e.target.value })}
                placeholder={'12-month wall calendar\n11" x 8.5", opens to 11" x 17"\nPrinted on heavyweight matte paper\nWire-o binding for easy hanging'}
                style={{ minHeight: '120px' }}
              />
              <small style={{ color: '#666' }}>Shown as a bulleted list under "Product Details" on the sales page</small>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Quantity in Stock</label>
                <input
                  type="number"
                  min="0"
                  className="form-input"
                  value={formData.stock ?? ''}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Cover Image</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-input"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Image URL or upload below"
                  style={{ flex: 1 }}
                />
                <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" style={{ marginTop: '10px', maxWidth: '200px', borderRadius: '8px' }} />
              )}
            </div>

            <div className="form-group">
              <label>Gallery Photos (one URL per line — cover photo, month spreads, etc.)</label>
              <textarea
                className="form-textarea"
                value={formData.additional_images}
                onChange={(e) => setFormData({ ...formData, additional_images: e.target.value })}
                placeholder="One image URL per line"
                style={{ minHeight: '100px' }}
              />
              <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '8px' }}>
                <Upload size={16} />
                {uploadingGallery ? 'Uploading...' : 'Upload Photo to Gallery'}
                <input type="file" accept="image/*" onChange={handleGalleryUpload} style={{ display: 'none' }} />
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.pre_sale}
                  onChange={(e) => setFormData({ ...formData, pre_sale: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                Pre-Sale (shows a "Pre-Order" badge instead of normal Add to Cart)
              </label>
            </div>

            {formData.pre_sale && (
              <div className="form-group">
                <label>Ships By</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.ships_by}
                  onChange={(e) => setFormData({ ...formData, ships_by: e.target.value })}
                  placeholder="e.g., mid-November 2026"
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    style={{ width: '20px', height: '20px' }}
                  />
                  Active (visible in shop)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary">
                <Save size={18} /> {editingProduct ? 'Update' : 'Create'} Calendar
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Products List */}
      {loading ? (
        <div className="loading-screen">
          <div className="loading-spinner"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <CalendarIcon size={48} strokeWidth={1} />
          <h3>No calendars yet</h3>
          <p>Add your first calendar to get started</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Sold</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.title} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ width: '60px', height: '60px', background: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CalendarIcon size={24} color="#ccc" />
                      </div>
                    )}
                  </td>
                  <td>
                    <strong>{product.title}</strong>
                    {product.tagline && <div style={{ fontSize: '13px', color: '#666' }}>{product.tagline}</div>}
                    {product.slug && <div style={{ fontSize: '12px', color: '#999' }}>/shop/calendar/{product.slug}</div>}
                  </td>
                  <td>${product.price?.toFixed(2)}</td>
                  <td style={{ color: product.stock === 0 ? '#dc3545' : 'inherit' }}>
                    {product.stock === null || product.stock === undefined ? '∞' : product.stock}
                  </td>
                  <td>
                    {salesStats[product.id] ? (
                      <div>
                        <strong>{salesStats[product.id].units_sold}</strong>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          ${salesStats[product.id].revenue.toFixed(2)} · {salesStats[product.id].order_count} order{salesStats[product.id].order_count === 1 ? '' : 's'}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>0</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
                        background: product.active ? '#d4edda' : '#f8d7da',
                        color: product.active ? '#155724' : '#721c24'
                      }}>
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                      {product.pre_sale && (
                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', background: '#fef3c7', color: '#92400e' }}>
                          Pre-Sale
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => handleEdit(product)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a' }} title="Edit">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(product.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }} title="Delete">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminCalendar;
